import type {
  PublicCategoryStatus,
  PublicHistoryDay,
  PublicIncidentSummary,
  PublicPlatform,
  PublicProviderStatus,
  PublicServiceDetail,
  PublicServiceStatus,
  PublicSparklineKind,
  PublicStatusResponse,
  PublicSummary,
} from "@/v2-status-client/status/status.types"

// normalizeStatusResponse coerces whatever the public-status API
// hands back into a fully-populated PublicStatusResponse the rest
// of the page can iterate over without defensive `?? []` guards
// every time it touches an array.
//
// Why this exists: `/public/status` is served by v2-status-api,
// which is deployed as its own Azure Web App. There are real
// rollout windows where the bundle in front of the customer is
// newer than the API behind it (the API redeploy can take a few
// minutes to finish; the slot swap can fail; the build can be
// stuck on CI). When the bundle expects a field the API hasn't
// learned about yet -- top-level `platforms` was the original
// case -- the page used to throw "e is not iterable" on first
// render and the customer saw a blank screen.
//
// The contract is intentionally generous: anything missing or the
// wrong shape is replaced with a safe empty value so every consumer
// downstream can assume a well-formed payload. Categories without a
// platformId fall through to a placeholder id; the orphan-fallback
// in `StatusPlatformList` then bundles them into a synthesised
// "Other services" platform so they still render.
export function normalizeStatusResponse(raw: unknown): PublicStatusResponse {
  const obj = isObject(raw) ? raw : {}
  return {
    summary: normalizeSummary(obj.summary),
    platforms: toArray(obj.platforms).map(normalizePlatform),
    categories: toArray(obj.categories).map(normalizeCategory),
    providers: toArray(obj.providers).map(normalizeProvider),
    incidents: toArray(obj.incidents).map(normalizeIncident),
    generatedAt: typeof obj.generatedAt === "string" ? obj.generatedAt : "",
  }
}

// Stable id used when a category arrives without a platformId. The
// "uncategorised" matches `ORPHAN_PLATFORM_ID` in StatusPlatformList
// so the existing orphan-fallback path picks these up and renders
// them under a synthesised "Other services" card; we deliberately
// don't import it to keep this module's dependency graph small
// (the value is part of the public contract of the orphan path
// either way).
const FALLBACK_PLATFORM_ID = "uncategorised"

function normalizeSummary(raw: unknown): PublicSummary {
  const o = isObject(raw) ? raw : {}
  return {
    totalServices: toInt(o.totalServices),
    servicesUp: toInt(o.servicesUp),
    servicesDown: toInt(o.servicesDown),
    servicesDegraded: toInt(o.servicesDegraded),
    coreTotalServices: toInt(o.coreTotalServices),
    coreServicesUp: toInt(o.coreServicesUp),
    coreServicesDown: toInt(o.coreServicesDown),
    avgLatencyMs: toNullableNumber(o.avgLatencyMs),
    avgLatencyIsBaseline: o.avgLatencyIsBaseline === true,
    uptimePercent30d: toNullableNumber(o.uptimePercent30d),
    incidentsLast30d: toInt(o.incidentsLast30d),
    incidentsOngoing: toInt(o.incidentsOngoing),
    lastIncidentAt:
      typeof o.lastIncidentAt === "string" ? o.lastIncidentAt : undefined,
  }
}

function normalizePlatform(raw: unknown): PublicPlatform {
  const o = isObject(raw) ? raw : {}
  return {
    id: toStr(o.id),
    name: toStr(o.name),
    description: toStr(o.description),
    status: toStatus(o.status),
    summary: toStr(o.summary),
    operational: toInt(o.operational),
    degraded: toInt(o.degraded),
    down: toInt(o.down),
    totalCount: toInt(o.totalCount),
    uptimePercent30d: toNullableNumber(o.uptimePercent30d),
    history: toArray(o.history).map(normalizeHistoryDay),
    categoryIds: toArray(o.categoryIds).filter(
      (s): s is string => typeof s === "string",
    ),
  }
}

function normalizeCategory(raw: unknown): PublicCategoryStatus {
  const o = isObject(raw) ? raw : {}
  const subCategoriesRaw = o.subCategories
  return {
    id: toStr(o.id),
    // platformId is the field most likely to be missing on a stale
    // backend. Keep `uncategorised` so the orphan path renders it
    // rather than dropping the category from the page.
    platformId:
      typeof o.platformId === "string" && o.platformId.length > 0
        ? o.platformId
        : FALLBACK_PLATFORM_ID,
    name: toStr(o.name),
    description: toStr(o.description),
    status: toStatus(o.status),
    summary: toStr(o.summary),
    operational: toInt(o.operational),
    degraded: toInt(o.degraded),
    down: toInt(o.down),
    totalCount: toInt(o.totalCount),
    history: toArray(o.history).map(normalizeHistoryDay),
    uptimePercent30d: toNullableNumber(o.uptimePercent30d),
    services: toArray(o.services).map(normalizeService),
    // Optional on the wire; only emit the field when the backend
    // actually carries one so React doesn't churn on a phantom
    // empty array vs `undefined`.
    subCategories: Array.isArray(subCategoriesRaw)
      ? subCategoriesRaw.map(normalizeCategory)
      : undefined,
  }
}

function normalizeService(raw: unknown): PublicServiceDetail {
  const o = isObject(raw) ? raw : {}
  const sparklineRaw = toArray(o.latencySparkline)
  return {
    id: toStr(o.id),
    name: toStr(o.name),
    description: typeof o.description === "string" ? o.description : undefined,
    host: typeof o.host === "string" ? o.host : undefined,
    providerName:
      typeof o.providerName === "string" ? o.providerName : undefined,
    status: toStatus(o.status),
    summary: toStr(o.summary),
    uptimePercent30d: toNullableNumber(o.uptimePercent30d),
    avgDurationMs: toNullableNumber(o.avgDurationMs),
    p95DurationMs: toNullableNumber(o.p95DurationMs),
    latencySparkline: sparklineRaw.map((v) =>
      typeof v === "number" && Number.isFinite(v) ? v : null,
    ),
    history: toArray(o.history).map(normalizeHistoryDay),
    latencySparklineKind: toSparklineKind(o.latencySparklineKind),
  }
}

function normalizeProvider(raw: unknown): PublicProviderStatus {
  const o = isObject(raw) ? raw : {}
  return {
    id: toStr(o.id),
    name: toStr(o.name),
    status: toStatus(o.status),
    summary: toStr(o.summary),
    operational: toInt(o.operational),
    degraded: toInt(o.degraded),
    down: toInt(o.down),
    totalCount: toInt(o.totalCount),
    history: toArray(o.history).map(normalizeHistoryDay),
  }
}

function normalizeIncident(raw: unknown): PublicIncidentSummary {
  const o = isObject(raw) ? raw : {}
  return {
    id: toStr(o.id),
    affectedScope: toStr(o.affectedScope),
    title: toStr(o.title),
    startedAt: toStr(o.startedAt),
    endedAt: typeof o.endedAt === "string" ? o.endedAt : undefined,
    durationMinutes: toInt(o.durationMinutes),
    state: o.state === "ongoing" ? "ongoing" : "resolved",
  }
}

function normalizeHistoryDay(raw: unknown): PublicHistoryDay {
  const o = isObject(raw) ? raw : {}
  return {
    date: toStr(o.date),
    status: toStatus(o.status),
    uptimePercent: toNullableNumber(o.uptimePercent),
    incidentMinutes: toInt(o.incidentMinutes),
  }
}

// ---------------------------- Primitives ----------------------------

function isObject(raw: unknown): raw is Record<string, unknown> {
  return raw !== null && typeof raw === "object" && !Array.isArray(raw)
}

function toArray(raw: unknown): unknown[] {
  return Array.isArray(raw) ? raw : []
}

function toStr(raw: unknown): string {
  return typeof raw === "string" ? raw : ""
}

function toInt(raw: unknown): number {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : 0
}

function toNullableNumber(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null
}

function toStatus(raw: unknown): PublicServiceStatus {
  if (raw === "down" || raw === "degraded" || raw === "operational") return raw
  // Unknown / missing status is treated as operational rather than
  // down so a fresh / unwired probe doesn't redshift the page on
  // its own. The platform/category rollups will already flag any
  // real outage from the rest of the payload.
  return "operational"
}

function toSparklineKind(raw: unknown): PublicSparklineKind | undefined {
  if (raw === "live" || raw === "baseline" || raw === "none") return raw
  return undefined
}
